import type { ReactNode } from 'react';
import { X } from 'lucide-react';
import {
    Button,
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    Tooltip,
    TooltipContent,
    TooltipTrigger,
    cn,
} from 'shared-ui';

import { useMediaQuery } from '@/hooks/useMediaQuery';

interface PanelCloseButtonProps {
    onClick: () => void;
    'aria-label': string;
    iconStrokeWidth?: number;
    absoluteIconStroke?: boolean;
}

export function PanelCloseButton({
    onClick,
    'aria-label': ariaLabel,
    iconStrokeWidth = 2.25,
    absoluteIconStroke = false,
}: PanelCloseButtonProps) {
    return (
        <Tooltip>
            <TooltipTrigger
                render={
                    <Button
                        variant="ghost"
                        size="icon"
                        className="panel-close-btn size-10"
                        onClick={onClick}
                        aria-label={ariaLabel}
                    >
                        {/* size PROP as well as class: absoluteStrokeWidth divides by the prop, so
                            without it an absolute stroke is a no-op and the CSS downscale thins it
                            (1.5 renders 1px). Harmless on the scaled default path. */}
                        <X
                            className="text-primary size-4"
                            size={16}
                            strokeWidth={iconStrokeWidth}
                            absoluteStrokeWidth={absoluteIconStroke}
                        />
                    </Button>
                }
            />
            <TooltipContent side="top">Close</TooltipContent>
        </Tooltip>
    );
}

interface SlidingPanelProps {
    isOpen: boolean;
    onClose: () => void;
    /** Accessible label for the desktop aside element. */
    ariaLabel: string;
    /** Screen-reader title shown inside the mobile Sheet. */
    title: string;
    /** Desktop open width (Tailwind class). Defaults to `w-90` (360px). */
    widthClassName?: string;
    children: ReactNode;
}

/**
 * Responsive right-side sliding panel shell used by MapLayersPanel, FiltersPanel,
 * StatsConfigPanel and the download panels. On desktop it renders as a fixed-width aside
 * that transitions from 0 to its open width (`widthClassName`, default 360 px); on mobile
 * it renders as a bottom-anchored Sheet.
 */
export function SlidingPanel({
    isOpen,
    onClose,
    ariaLabel,
    title,
    widthClassName = 'w-90',
    children,
}: SlidingPanelProps) {
    const isDesktop = useMediaQuery('(min-width: 768px)');

    if (isDesktop) {
        return (
            <aside
                aria-label={ariaLabel}
                aria-hidden={!isOpen}
                inert={!isOpen}
                className={cn(
                    'bg-background flex h-full shrink-0 flex-col overflow-hidden transition-[width] duration-200 ease-in-out',
                    isOpen ? cn(widthClassName, 'border-l') : 'w-0',
                )}
            >
                {children}
            </aside>
        );
    }

    return (
        <Sheet
            open={isOpen}
            onOpenChange={(open) => {
                if (!open) onClose();
            }}
        >
            <SheetContent
                side="right"
                showCloseButton={false}
                className="flex flex-col gap-0 p-0 data-[side=right]:w-[90vw]"
            >
                <SheetHeader className="sr-only">
                    <SheetTitle>{title}</SheetTitle>
                </SheetHeader>
                {children}
            </SheetContent>
        </Sheet>
    );
}
